import { RcFile } from 'antd/es/upload'
import { UploadFile } from 'antd/lib'
import { AxiosResponse } from 'axios'
import React, { Key, ReactNode, SetStateAction } from 'react'

export type SVGIcon = React.FunctionComponent<React.SVGProps<SVGSVGElement> & { title?: string }>

export type User = {
  id: number
  userName: string
  roles: Role[]
  userDetail: UserDetail
  company: Company
}
export type Company = {
  shortName: string
  gmtZone: number
}

export enum RoleEnum {
  ADMIN = 'ADMIN',
  SUPPLIER = 'SUPPLIER',
  CARRIER = 'CARRIER',
  PROCUREMENT = 'PROCUREMENT',
  ADMINRO = 'ADMINRO',
}

export enum WebTitleEnum {
  TITLE = 'MLO România',
}

export type Role = {
  id: number
  name: RoleEnum
  description: string
}

export type ComboOption<T = number | string> = {
  label: string
  value: T
}

export type ComboOptionNumber = {
  label: string
  value: number
}

type UserDetail = {
  id: number
  name: string
  surname: string
  email: string
  supplier?: Supplier
  carrier?: Carrier
}

export type Carrier = {
  id: number
  createAt: string
  tradeName: string
  businessName: string
  country?: string
  email?: string
  cellphone?: string
  phone?: string
  address: string
  category: number
  enabledForAlgorithm: boolean
  location: Location
  sapId: number
  trucks: Truck[]
  drivers: Driver[]
  trailers: Trailer[]
}

type Location = {
  type: string
  coordinates: [number, number]
}

export type Supplier = {
  key: SetStateAction<string | null>
  id: number
  tradeName: string
  businessName: string
  country?: string
  taxId: string
  email?: string
  cellphone?: string
  phone?: string
  depots: Depot[]
  sapId: number
  zone: string
}

export type SupplierFilters = {
  page?: number
  limit?: number
  zone?: string[]
}

export interface Depot extends EntityWithId {
  origin: any
  tag: string
  address: string
  description?: string
  street: string
  number: string
  location: Location
  city: string
  state: string
  sapGeo: SapGeo
}

export type SapGeo = {
  id: number
  cityCode: string
  postCode: string
  cp: string
  description: string
  region: string
  zone: string
  country: string
}

export type Destination = {
  id: number
  street: string
  tag: string
  city: string
  number: string
  state: string
  country: string
  plantId: string
}
export interface SapContract {
  id: number
  createAt: string
  contract: string
  position: string
  subPosition: string
  description: string
  quantity: string
  mu: string
  price: string
  currency: string
  per: string
  asnumId: string
  dateValidityAgreementFrom: string
  dateValidityAgreementTo: string
  carrier: Carrier
}
export type Contracts = {
  contracts: SapContract[]
  total: number
}

export type ContractJobUpdate = {
  id: number
  createAt: string
  type: string
  message: string
}

export enum TicketAssignment {
  PENDING = 'PENDING',
  CONFIRMED = 'CONFIRMED',
  DECLINED = 'DECLINED',
}
export enum CarrierModeEnum {
  RAILCAR = 'RAILCAR',
  TRUCK = 'TRUCK',
}

export interface TruckDocumentation {
  BL: string[]
  id: number
  invoiceDate: string[]
  invoiceNumber: string
  invoiceAmount: number
  materialId: string
  materialDescription: string
  purchaseOrderId: string
  createAt: string
  photos: string[]
  salesInvoice: string[]
  weightCertificate: string[]
  radioactiveCertificate: string[]
  camion: string
}
export interface RailcarDocumentation {
  waybill: string
  carEquipment: string
  id: number
  invoiceDate: string[]
  invoiceNumber: string
  invoiceAmount: number
  materialId: string
  materialDescription: string
  purchaseOrderId: string
  createAt: string
  photos: string[]
  comments: string
  salesInvoice: string[]
  weightCertificate: string[]
  BL: string[]
  radioactiveCertificate: string[]
}
export enum StatusTicketEnum {
  CREATED = 'CREATED',
  PENDING = 'PENDING',
  CONFIRMED = 'CONFIRMED',
  INVOICED = 'INVOICED',
  CANCELED = 'CANCELED',
  TRANSIT = 'TRANSIT',
  DELIVERED = 'DELIVERED',
  AWAITING = 'AWAITING',
  RELEASED = 'RELEASED',
  SUPPLIER_INVOICED = 'SUPPLIER_INVOICED',
  REQUEST = 'REQUEST',
  APPROVED = 'APPROVED',
  NO_SHOW = 'NO_SHOW',
}

export type TicketAssignmentType = {
  id: number
  status: {
    id: number
    name: TicketAssignment
  }
  carrier: Carrier
  autoAssign: boolean
  sapItem: {
    id: number
    subPosition: number
    description: string
  }
}

export type Ticket = {
  id: number
  supplier: Supplier
  comments: string
  status: {
    name: StatusTicketEnum | TicketAssignment
  }
  material: Material
  deliveryNote: string
  equipment: Equipment
  createAt: Date
  carrier: Carrier
  withdrawalDate: Date
  ticketAssignments: TicketAssignmentType[]
  destination: {
    tag: string
    id: number
    plantId: string
    location: Location
    street: string
    number: string
    city: string
    state: string
  }
  truckDocumentation?: TruckDocumentation
  origin: Depot
  invoice?: Invoice
  customsRead: boolean
  pendingDate?: string
  withdrawalDay?: string
  withdrawalTime?: string
  withdrawalDayOfYear?: number
  notes: Note[]
  observations: Note[]
  documentNumberId: string
  sapItemSupplier?: SapItemSupplier
  file: FileType[]
  countFile: number
  driver?: Driver
  truck: Truck
  trailer: Trailer
  autoAssign: boolean
  tripType: TripTypeEnum
  ticketSap?: string
  driverAssignedAt: Date
  driverCheckInPoint?: string
  driverCheckOutPoint?: string
  tripStartDate?: string
  checkOutDate?: string
  tripEndDate?: string
  distanceTripStartDepot?: number
  declinedLoadAt?: Date
  declinedLoadType?: DeclinedLoadTypeEnum
  textractData?: string
}

export type FileType = {
  createAt: string
  fileName: string
  id: number
  observations: string
  status: string
  ticketFile: string
}

export type Observation = {
  createAt?: Date
  description?: string
  id?: number
  type?: string
  user?: User
  driver?: number
  truck?: number
}

export type Equipment = {
  id: number
  name: string
  nameShort: string
  volumeMax: string
  color: string
  straightTruck?: boolean
  highDemand: boolean
  equipmentType: EquipmentType | null
}

export type EquipmentType = {
  id: number
  licensePlate: string
  nameShort: string
  description: string
}

export type SapItemSupplierDeliveryNote = {
  id: number
  deliveryNote: string
  quantity: string
  createAt?: Date
  updateAt?: Date
  entryDate?: Date
}

export type Material = {
  id: number
  nameSap: string
  nameShort: string
  nameSupplier: string
  sapCode: string
  sapItemSupplier?: SapItemSupplier
}

export type GroupMaterial = {
  id: number
  name: string
  description: string
}

export type CarrierEquipment = {
  id: number
  brand: string
  model: string
  plate: string
  equipment: Equipment
}

export type Invoice = {
  createAt: string
  date: string
  deleteAt?: string
  id: number
  invoiceFile: string
  number: string
  paymentNumber: string
  paymentDate: string
  paymentFile: string
  ticket: Ticket
  rpa: RPA
  sapValidationStatus: InvoiceCheckEnum
  sapValidationDate: Date
}

export type RPA = {
  id: number | '-'
  from: string
  to: string
  invoiceQty: number
}

export interface RPAFilters extends BaseFilters {
  rpaId?: string
  date?: Date
}

export type Contract = {
  id?: number
  description?: string
  price: number
  from?: Date
  to: Date
  carrier: Carrier
}

export interface Route {
  id: number
  createAt: string
  asnum: number
  vsart: string
  add01: string
  add02: string
  land1a: string
  pstlza: string
  zcityCodea: string
  trfzna: string
  regioa: string
  land1z: string
  pstlzz: string
  zcityCodez: string
  trfznz: string
  regioz: string
  servTyp: string
  sincroDepot: { tag: string }
}

export type Routes = {
  routes: Route[]
  total: number
}

export interface EntityWithId {
  id: number
}
export interface BaseEntity extends EntityWithId {
  createAt?: Date
  updateAt?: Date
  deleteAt?: Date
}

export interface BaseFilters {
  limit?: number
  page?: number
  asnum?: number
}

export interface Filters extends BaseFilters {
  statusName?: string[]
  supplierId?: number[]
  carrierId?: number[]
  destinationId?: number[]
  equipmentId?: number[]
  orderBy?: string
  ticketId?: string
  invoiceNumber?: string
  taInvoiceNumber?: string
  materialId?: number[]
  mondayOfWeek?: Date | undefined
  withdrawalDate?: Date | undefined
  dateFrom?: Date | undefined
  dateTo?: Date | undefined
  evouchers?: boolean
  previous?: boolean
  zone?: string[]
}

export interface ContractFilters extends BaseFilters {
  contract?: string
  carrierId?: number
  asnum?: number
  orderBy?: 'ASC' | 'DESC' | undefined
}
export interface VehiclesFilters extends BaseFilters {
  carrierId?: number
  orderBy?: 'ASC' | 'DESC' | undefined
}
export interface DriversFilters extends BaseFilters {
  carrierId?: number
  orderBy?: 'ASC' | 'DESC' | undefined
}

export interface FiltersValues {
  filters: FilterObject
  setFilters: (value: FilterObject | ((state: FilterObject) => FilterObject)) => void
  handleFilters: ({
    key,
    value,
    pageReset,
  }: {
    key: string
    value?: FilterValue
    pageReset?: boolean
  }) => void
  handleResetFilters: () => void
  resetFilters: number
  displayFilters: boolean
  handleFilterChange: (key: string, pageReset?: boolean) => (value: any) => void
  filtersToShow?: FilterObject
}

export interface TrailersFilters extends BaseFilters {
  carrierId?: number
  orderBy?: 'ASC' | 'DESC' | undefined
}
export interface RoutesFilters extends BaseFilters {
  active?: boolean
  routes?: string
  asnum?: number
  contract?: string
  carrierId?: number
  originPostCode?: string // PSTLZA
  originCityCode?: string // ZCITY_CODEA
  originZone?: string // TRFZNA
  originRegion?: string // REGIOA
  destinationPostCode?: string // PSTLZZ
  destinationCityCode?: string // ZCITY_CODEZ
  destinationZone?: string // TRFZNZ
  destinationRegion?: string // REGIOZ
  orderBy?: 'ASC' | 'DESC' | undefined
  limit?: number | undefined
  page?: number | undefined
  equipment?: string | undefined
}

export interface RoutesFiltersResponse {
  sapTptAsnums: number[]
  originZones: string[]
  destinationZones: string[]
  originRegions: string[]
  destinationRegions: string[]
  originCityCodes: {
    cityCode: string
    region: string
    description: string
  }[]
  destinationCityCodes: {
    cityCode: string
    region: string
    description: string
  }[]
  originPostCodes: string[]
  destinationPostCodes: string[]
}

export type ContractsAsnumsResponse = string[]
export type ContractsFilterResponse = number[]

export enum FilterSelectEnum {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  ASCENDING = 'ASC',
  DESCENDING = 'DESC',
}

export interface CarrierFilters extends BaseFilters {
  carrierId?: number[]
  limit: number
}

export interface CarriersFilters extends BaseFilters {
  limit: number
  orderBy: string
  carriers?: number[]
}

export enum ConfigurationPropertiesEnum {
  ALLOCATION_FREQUENCY = 'ALLOCATION_FREQUENCY',
  PRICE_PERCENTAGE = 'PRICE_PERCENTAGE',
  AUTOMATIC_ASSIGNMENT = 'AUTOMATIC_ASSIGNMENT',
  SAML_ACTIVE = 'SAML_ACTIVE',
  SUPPLIER_CAN_CREATE_AUTOMATIC_TICKETS = 'SUPPLIER_CAN_CREATE_AUTOMATIC_TICKETS',
  USE_CARRIER_AVAILABILITY = 'USE_CARRIER_AVAILABILITY',
  REQUIRE_TRUCK_AND_TRAILER = 'REQUIRE_TRUCK_AND_TRAILER',
  MAX_ALLOWED_DAYS_FOR_SUPPLIER_REQUEST = 'MAX_ALLOWED_DAYS_FOR_SUPPLIER_REQUEST',
  REQUIRE_DRIVER = 'REQUIRE_DRIVER',
}

export enum InvoiceCheckEnum {
  CONFIRMED = 'CONFIRMED',
  PENDING = 'PENDING',
  REJECTED = 'REJECTED',
}

export type ConfigurationProperties = {
  id: number
  createAt: string
  key: string
  value: string
}

export type DeclineTicket = { reason: string }

export type DeliveryNote = { number: string; observation?: string }

export enum NotificationEnum {
  NEW_TICKET_ADMIN = 'NEW_TICKET_ADMIN',
  NEW_TICKET_CARRIER = 'NEW_TICKET_CARRIER',
  NEW_TICKET_SUPPLIER = 'NEW_TICKET_SUPPLIER',
  AUTHORIZED = 'AUTHORIZED',
  DECLINED = 'DECLINED',
  CONFIRMED = 'CONFIRMED',
  CANCELLED_REALLOCATED_TICKET = 'CANCELLED_REALLOCATED_TICKET',
  WAITING_APPROVAL = 'WAITING_APPROVAL',
  TRANSIT = 'TRANSIT',
}

export type PurchaseOrder = {
  documentNumberId: string
  position: string
  holding: string
  companyId: string
  companyDesc: string
  buyerDesc: string
  materialId: string
  materialDesc: string
  proposedDeliveryDate: string
  plantId: string
  plantDesc: string
  requestedQuantity: string
  entryQuantity: string
  preEntryQuantity: string
  inTransitQuantity: string
  availableQuantity: string
  um: string
  warehouseId: string
  finalDestinationId: string
  tolerance: string
  earlyDeliveryDays: string
  avaliableCheck: string
  earlyDeliveryDate: string
  supplierCode: string
}
export type SapItemSupplier = {
  DocumentNumberID: string
  ProposedDeliveryDate: string
  AvailableQuantity: string
  EarlyDeliveryDate: string
  Inco1: string
  Inco2: string
  disabledAt: Date | null
  PlantID: string
  sapItemSupplierDeliveryNotes?: SapItemSupplierDeliveryNote[]
}

export type SupplierPurchaseOrder = {
  id: number
  PurchaseDocumentNumber: string
  material: Material
  MaterialDesc: string
  ExirosRef: string
}
export interface DayToPushProps {
  date: string
  tickets: Ticket[]
}

export interface Note {
  id: Key | null | undefined
  description: string
  createAt: string
  updateAt: string
  user: User
}

export interface NoteToCreate {
  description?: string
}

export type AddNoteType = {
  id: number
  note: Observation
}

export const PATHS = {
  ADMIN: {
    ONGOING: '/dashboard/admin/ongoing',
    EVOUCHERS: '/dashboard/admin/evouchers',
    OPEN: '/dashboard/admin/open',
    CLOSED: '/dashboard/admin/closed',
    CERTIFIED: '/dashboard/admin/certified',
    ROUTES: '/dashboard/admin/routes',
    CONTRACTS: '/dashboard/admin/contracts',
    DRIVERS: '/dashboard/admin/drivers',
    VEHICLES: '/dashboard/admin/vehicles',
    TRAILERS: '/dashboard/admin/trailers',
    SETTINGS: '/dashboard/admin/settings',
    SIMULATION: '/dashboard/admin/simulation',
    CARRIERS: '/dashboard/admin/carriers',
    SUPPLIERS: '/dashboard/admin/suppliers',
    BLOCKTRAINS: '/dashboard/admin/blocktrains',
    PREVIOUS: '/dashboard/admin/previous',
    HISTORIC: '/dashboard/admin/historic',
  },
  SUPPLIER: {
    HOME: '/dashboard/suppliers',
    PREVIOUS: '/dashboard/suppliers/previous',
    BLOCKTRAINS: '/dashboard/suppliers/blocktrains',
    SETTINGS: '/dashboard/suppliers/settings',
    ONGOING: '/dashboard/suppliers/ongoing',
    HISTORIC: '/dashboard/suppliers/historic',
  },
  CARRIER: {
    HOME: '/dashboard/carrier',
    ALL: '/dashboard/carrier/all',
    EVOUCHERS: '/dashboard/carrier/evouchers',
    VEHICLES: '/dashboard/carrier/vehicles',
    DRIVERS: '/dashboard/carrier/drivers',
    BATCHES: '/dashboard/carrier/batchees',
    AVAILABILITY: '/dashboard/carrier/availability',
  },
  PROCUREMENT: {
    ROUTES: '/dashboard/procurement/routes',
    CONTRACTS: '/dashboard/procurement/contracts',
    SIMULATION: '/dashboard/procurement/simulation',
    CARRIERS: '/dashboard/procurement/carriers',
    DRIVERS: '/dashboard/procurement/drivers',
    VEHICLES: '/dashboard/procurement/vehicles',
    TRAILERS: '/dashboard/procurement/trailers',
    SUPPLIERS: '/dashboard/procurement/suppliers',
  },
  ADMINRO: {
    ONGOING: '/dashboard/adminro/ongoing',
    EVOUCHERS: '/dashboard/adminro/evouchers',
    OPEN: '/dashboard/adminro/open',
    CLOSED: '/dashboard/adminro/closed',
    CERTIFIED: '/dashboard/adminro/certified',
    ROUTES: '/dashboard/adminro/routes',
    CONTRACTS: '/dashboard/adminro/contracts',
    DRIVERS: '/dashboard/adminro/drivers',
    VEHICLES: '/dashboard/adminro/vehicles',
    TRAILERS: '/dashboard/adminro/trailers',
    SETTINGS: '/dashboard/adminro/settings',
    SIMULATION: '/dashboard/adminro/simulation',
    CARRIERS: '/dashboard/adminro/carriers',
    SUPPLIERS: '/dashboard/adminro/suppliers',
    BLOCKTRAINS: '/dashboard/adminro/blocktrains',
    PREVIOUS: '/dashboard/adminro/previous',
    HISTORIC: '/dashboard/adminro/historic',
  },
}

export interface Notifications {
  id: number
  message: string
  ticket?: {
    id: number
    status: StatusTicketEnum
  }
}

export enum DataUploadStatusEnum {
  CREATED = 'CREATED',
  IN_PROCESS = 'IN_PROCESS',
  PROCESSED = 'PROCESSED',
  PROCESSED_WITH_ERRORS = 'PROCESSED_WITH_ERRORS',
  NOT_PROCESSED = 'NOT_PROCESSED',
}

export enum TripTypeEnum {
  FOB = 'FOB',
  TRANSFERENCE = 'TRANSFERENCE',
}

export type OrderByType = {
  field?: string
  order?: string
}
export type DataUploadHistoryType = {
  routes: RoutesFile[]
  total: number
}

export type RoutesFile = {
  id: number
  createAt: Date
  fileName: string
  status: DataUploadStatusEnum
  observations?: string
  routesFile: string
}

export type BulkHistoryDataFilters = {
  limit: number
  page: number
  orderBy?: string
}
export enum BulkDataTypesEnum {
  ROUTES = 'ROUTES',
}

export enum DeclinedLoadTypeEnum {
  RETURNED_TO_ORIGIN = 'RETURNED_TO_ORIGIN',
  EQUIPMENT_FREED = 'EQUIPMENT_FREED',
  RE_ENTRY_TO_DEPOT = 'RE_ENTRY_TO_DEPOT',
}

export type SimulationType = {
  id: number
  createAt: string
  tradeName: string
  businessName: string
  country: string
  email: string
  cellphone: string
  phone: string
  address: string
  category: number
  priceCategory?: number
  enabledForAlgorithm: boolean
  sapId: number
  sapItems: {
    id: number
    createAt: string
    contract: string
    position: number
    subPosition: number
    description: string
    quantity: string
    mu: string
    price: string
    currency: string
    per: string
    asnumId: string
    dateValidityAgreementFrom: string
    dateValidityAgreementTo: string
  }[]
}

export type AxiosErrorType = {
  response: any
  data: {
    error: {
      type: string
      message: string
    }
  }
  status: number
}

export interface TicketResponse {
  tickets: Ticket[]
  total: number
}

export interface GetTicketsResponse {
  tickets?: TicketResponse
  ticket?: Ticket
  error?: AxiosResponse
  mondayOfWeek: Date
}

export interface FormTypeSupplier {
  count: number
  materialId: number
  supplierId?: number
  equipmentId: number
  destinationId: number
  depotId: number
  type: string
  note?: NoteToCreate
  ticketsDate?: Date | Date[] | undefined
  ticketDate?: Date | undefined
  carrierId?: number
  documentNumberId?: string
  declinedLoad?: boolean
  declinedLoadType?: DeclinedLoadTypeEnum
}

export interface Truck {
  available: boolean
  id: number
  licensePlate: string
  description: string
  disableAt: boolean
  straightTruck: boolean
  equipmentType: {
    name: string
    id: number
    nameShort: string
    straightTruck: boolean
  } | null
  carrier: Carrier
  enabled?: boolean
}

export type Trucks = Truck[]

export interface Trailer {
  available: boolean
  id: number
  licensePlate: string
  description: string
  disableAt: boolean
  equipmentType: {
    name: string
    id: number
    nameShort: string
    straightTruck: boolean
  }
  carrier: Carrier
  enabled?: boolean
}

export type Trailers = Trailer[]

export type Vehicle = Trailer | Truck
export type Vehicles = Vehicle[]

export interface VehiclesResponse {
  trucks: {
    trucks: Trucks
    total: number
  }
}
export interface TrailersResponse {
  trailers: {
    trailers: Trailers
    total: number
  }
}

export interface DriversResponse {
  drivers: {
    drivers: Drivers
    total: number
  }
}

export interface UpdateTruckData {
  description: string
  disabled: boolean
  straightTruck: boolean
  equipmentType?: { id: number | null }
}

export interface SubmitUpdateTruckData {
  licensePlate: string
  description: string
  equipmentType: { id: unknown }
  disableAt: boolean
}

export interface Driver {
  name: ReactNode
  errorSendingValidationMessage: boolean
  phoneVerifiedAt: string | null
  available: boolean
  id: number
  firstName: string
  lastName: string
  carriers: Carrier[]
  stateIdNumber: string
  phone: string
  disableAt: boolean
  preferredTruck: Truck
  preferredTrailer: Trailer
  preferredTrucks: Trucks
  preferredTrailers: Trailers
}

export type Drivers = Driver[]

export interface PreferredTruck {
  description: string
  id: number
  licensePlate: string
}
export interface UpdateDriverData {
  phone: string
  preferredTruck: number
  preferredTrailer: number
  disabled: boolean
}

export interface UpdateDriverDataSubmit {
  preferredTrailers: {
    id: number
  }[]
  preferredTrucks: {
    id: number
  }[]
  disabled: boolean
  phone: string
}

export type UpdateTicket = {
  driverId?: number
  truckId?: number
  trailerId?: number
  deliveryNote?: { number: string; observation: string }
  documentNumberId: string
  materialId: number
  ticketSap?: string
}

export type TelephonePrefix = {
  prefix: string
  country: string
  description: string
  min: number
  max: number
  countryShortName: string
  countryEmoji: string
}

export type Batch = {
  carrier: {
    businessName: string
    id: number
  }
  closeDate: string
  createAt: string
  hes: string
  hesDate: string
  id: number
  openDate: string
  tickets: []
  updateAt: string
  status: string
}

export interface CarrierAvailability {
  equipment: Equipment
  quantity: number | null
}

export interface CarrierAvailabilityResponse {
  declarations: CarrierAvailability[]
}

export type AvailabilityDeclaration = {
  equipmentId: number
  quantity: number
}

export interface CarrierAvailabilityPayload {
  carrierAvailability: {
    date: string
    declarations: AvailabilityDeclaration[]
  }
}

export type EquipmentDataSummarized = {
  equipment: Equipment
  declared: number
  confirmed: number
  pending: number
  available: number
}

export type CarrierAvailabilitySummary = {
  carrier: Carrier
  equipmentDataSummarized: EquipmentDataSummarized[]
}

export type CarriersAvailabilitySummaryResponse = CarrierAvailabilitySummary[]

export interface ExpandableRowData {
  key: string
  tickets: Ticket[]
}

export type FilterValue = number | string | boolean | number[] | string[] | FilterObject

export interface FilterObject {
  [key: string]: FilterValue
}

export interface ApiParams {
  filters?: FilterObject
}

export interface GetSuppliersResponse {
  suppliers: Supplier[]
  totalCount: number
}

export interface GetSuppliersByFiltersResponse {
  suppliers: {
    data: Supplier[]
    total: number
    lastPage: number
    page: string
  }
}

export type BlocktrainType = {
  key: SetStateAction<string | null>
  id: number
  sapId: number
  tradeName?: string
  origin?: string
  destination?: string
  railcarsNumber?: string
  railcarsCO?: string
  deliveryNoteDate?: string
  railCarWayBill?: string
  status?: string
  data?: string
  total?: number
  zone?: string
  createAt?: string
  address?: string
  category?: number
  enabledForAlgorithm?: boolean
  location?: Location
  yard: string
  railCarQuantity: string
  supplier: any
}

export interface BlocktrainBase {
  supplier: EntityWithId
  origin: EntityWithId
  destination: EntityWithId
  railCarWayBill: string
  estimatedDateOfDeparture: Date
  estimatedTimeArrival: Date
  invoice: EntityWithId
  createBy: EntityWithId
  billingBatch: EntityWithId
  trainCompany: TrainCompany
  ticketSap: string
  comments?: string | null
  status: BlockTrainStatusEnum
}

export interface BlocktrainUpdatePayload extends Partial<BlocktrainBase> {}

export interface Blocktrain extends BaseEntity, BlocktrainBase {}

export interface BlocktrainDetail extends Blocktrain {
  railCarQuantity: number
  files?: BlockTrainFile[]
  railCars: Railcar[]
  trainCompany: TrainCompany
  origin: Depot
  supplier: Supplier
}

export interface RailcarBase {
  blockTrain: EntityWithId
  material: EntityWithId
  deliveryNote: string
  deliveryNoteDate: Date
  purchaseOrder: EntityWithId
  grossTn: number
  tareTn: number
  netTn: number
  railCarId: string
  copiedFiles?: number[]
  allFiles: RcFile[]
}

export interface RailcarFile extends BaseEntity {
  fileType?: string
  fileName?: string
  file: BaseFile
  type: RailCarFileType
  originFileObj?: RcFile
}

export interface RailCarFileType extends BaseEntity {
  name: string
  company: Company
  blockingStatus: BlockTrainStatusEnum
}

export interface Railcar extends BaseEntity {
  railCarNumber: number
  purchaseOrder: SupplierPurchaseOrder
  material: Material
  files: RailcarFile[]
}

export interface RailcarCreateData {
  payload: RailcarBase
  files?: (RcFile | undefined)[] | undefined
  types?: string[]
}

export interface RailcarUpdatePayload extends Partial<RailcarBase> {}

export interface Railcar extends BaseEntity, RailcarBase {}

export interface RailcarDetail extends Railcar {}

export interface RailcarId {
  id: string | number
  deliveryNote: string
}

export interface RailcarFilePayload {
  railcarId: number | undefined
  railcarFile: File
  fileTypeId: number | undefined
}
export interface BlockTrainFilePayload {
  blockTrainId: number | undefined
  blockTrainFile: File
  fileTypeId: number | undefined
}

export interface RailcarfileTypes {
  fileTypes: string[]
}
export interface BlockTrainFile extends BaseEntity {
  file: BaseFile
  type: BlockTrainFileType
}

export interface BlockTrainFileType extends BaseEntity {
  fileType: ReactNode
  name: string
  company: Company
  blockingStatus: BlockTrainStatusEnum
}
export interface BaseFile extends BaseEntity {
  name?: string
  type?: string
  uid?: any
  fileName: string
  observations: string
  ticketFile: string
}

export interface Train {
  tradeName: string
  email: string
  cellphone: string
  sapId: string
}
export interface CompanyObject {
  id: number
  createAt: Date
  updateAt: Date
  shortName: string
  name: string
  gmtZone: number
}

export interface TrainCompanyBase {
  company: CompanyObject
  shortName: string
  name: string
  email: string
  adress: string
  gmtZone: number
}

export interface TrainCompany extends BaseEntity, TrainCompanyBase {}

export interface TrainCompanyDetail extends TrainCompany {}

export enum BlockTrainStatusEnum {
  LOADING = 'LOADING',
  WAITING_APPROVAL = 'WAITING_APPROVAL',
  AUTHORIZED = 'AUTHORIZED',
  TRANSIT = 'TRANSIT',
  UNLOADING = 'UNLOADING',
  FINISHED = 'FINISHED',
  CANCELED = 'CANCELED',
}

export type BlocktrainStatusCount = {
  [key in BlockTrainStatusEnum]: number
}

export interface UpdateBlocktrainStatusPayload {
  id: number | string
  status: BlockTrainStatusEnum
  estimatedDateOfDeparture?: Date
  estimatedTimeArrival?: Date
}

export interface DateBlocktrainUpdatePayload {
  id: number | string
  estimatedDateOfDeparture?: Date
  estimatedTimeArrival?: Date
}
export interface UseUpdateBlocktrainDateReturn {
  updateBlocktrainDate: (data: {
    estimatedDateOfDeparture: string
    estimatedTimeArrival: string
  }) => void
  updateBlocktrainDateIsFetching: boolean
}

export type ScrapFile = UploadFile<RcFile> & {
  fileType: string
  id: number
  isCopied?: boolean
}

export enum FileTypeSelectEnum {
  DOC1 = 'DOC 1',
  DOC2 = 'DOC 2',
  DOC3 = 'DOC 3',
}

export type RailCarWaybill = {
  id: number | string
}

export interface GetAllBlocktrainsByFiltersResponse {
  blockTrains: {
    blockTrains: Blocktrain[]
    total: number
  }
}
